// Tần suất quảng cáo chèn trước video, tính riêng từng người xem trên máy này (spec 05/11 đợt 3):
// tối đa một quảng cáo mỗi 3 video người xem bấm xem, và hai quảng cáo cách nhau ít nhất 2 phút.
// Lưu ở localStorage, mọi lần đọc ghi đều bọc try/catch. Không dùng được localStorage (chế độ riêng tư, bị chặn) thì giữ trong
// bộ nhớ, để luật vẫn áp trong phiên này.

export const PRE_ROLL_EVERY_STARTS = 3;
export const PRE_ROLL_MIN_GAP_MS = 2 * 60 * 1000;
/** Hỏi mà không có quảng cáo thì chờ chừng này mới hỏi lại, để video không phải chờ 1,5 giây mỗi lần khi chưa có chiến dịch nào. */
export const PRE_ROLL_MISS_COOLDOWN_MS = 2 * 60 * 1000;
/** Lượt hỏi đang dở quá chừng này (trang gỡ giữa chừng) thì coi như đã xong, không khoá mãi. */
export const PRE_ROLL_IN_FLIGHT_STALE_MS = 60 * 1000;
const KEY = 'cab-preroll-freq';

interface FreqState {
  /** Số video đã bấm xem mà không có quảng cáo kể từ quảng cáo gần nhất. */
  startsSince: number;
  /** Lúc quảng cáo gần nhất thật sự bắt đầu chạy (ms), null nếu chưa có. */
  lastShownAt: number | null;
  /** Lúc gần nhất đã hỏi mà không có quảng cáo (ms), null nếu chưa có. */
  lastMissAt: number | null;
}

// Người xem mới coi như đã đủ số video, để video đầu tiên được phép có quảng cáo.
const fresh = (): FreqState => ({ startsSince: PRE_ROLL_EVERY_STARTS - 1, lastShownAt: null, lastMissAt: null });

let memory: FreqState | null = null;
/** Ghi localStorage đã từng lỗi trong phiên này: từ đó đọc bản trong bộ nhớ (bản trên đĩa có thể đã cũ). */
let storageBroken = false;
/** Lúc bắt đầu lượt hỏi đang dở (ms). Hai video bấm gần nhau không cùng được quảng cáo trong một lượt hỏi. */
let inFlightSince: number | null = null;

const parse = (raw: any): FreqState => {
  if (!raw || typeof raw !== 'object') return fresh();
  const num = (v: any) => (Number.isFinite(v) ? Number(v) : null);
  return {
    startsSince: Number.isFinite(raw.startsSince) ? Math.max(0, Number(raw.startsSince)) : fresh().startsSince,
    lastShownAt: num(raw.lastShownAt),
    lastMissAt: num(raw.lastMissAt),
  };
};

const read = (): FreqState => {
  if (storageBroken && memory) return memory;
  try {
    const stored = localStorage.getItem(KEY);
    if (stored != null) return parse(JSON.parse(stored));
    return memory ?? fresh();
  } catch {
    return memory ?? fresh();
  }
};

const write = (s: FreqState) => {
  memory = s;
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    storageBroken = true; // bộ nhớ đầy hoặc bị chặn: đã giữ trong bộ nhớ
  }
};

const inFlight = (now: number) => inFlightSince != null && now - inFlightSince < PRE_ROLL_IN_FLIGHT_STALE_MS;

const eligible = (s: FreqState, now: number) =>
  s.startsSince >= PRE_ROLL_EVERY_STARTS - 1 &&
  (s.lastShownAt == null || now - s.lastShownAt >= PRE_ROLL_MIN_GAP_MS) &&
  (s.lastMissAt == null || now - s.lastMissAt >= PRE_ROLL_MISS_COOLDOWN_MS);

/**
 * Người xem vừa bấm xem một video (không phải video của chính họ, không có Cab+ Pro). Trả true nếu lần này được phép hỏi
 * quảng cáo chèn trước; khi đó gọi tiếp markPreRollShown (quảng cáo đã chạy), markPreRollMissed (không có quảng cáo) hoặc
 * releasePreRollSlot (bỏ giữa chừng). Trả false thì lần bấm xem này đã được đếm.
 */
export const claimPreRollSlot = (now: number = Date.now()): boolean => {
  const s = read();
  if (!inFlight(now) && eligible(s, now)) {
    inFlightSince = now;
    return true;
  }
  write({ ...s, startsSince: s.startsSince + 1 });
  return false;
};

/** Đếm một lần bấm xem video không có quảng cáo mà không hỏi quảng cáo (ví dụ reel không rơi vào ô quảng cáo). */
export const notePlaybackStart = () => {
  const s = read();
  write({ ...s, startsSince: s.startsSince + 1 });
};

/** Quảng cáo chèn trước đã thật sự bắt đầu chạy. */
export const markPreRollShown = (now: number = Date.now()) => {
  inFlightSince = null;
  write({ ...read(), startsSince: 0, lastShownAt: now });
};

/** Đã được phép nhưng không có quảng cáo (máy chủ trả rỗng, quá hạn, lỗi, video quảng cáo không chạy được). */
export const markPreRollMissed = (now: number = Date.now()) => {
  inFlightSince = null;
  const s = read();
  write({ ...s, startsSince: s.startsSince + 1, lastMissAt: now });
};

/** Bỏ lượt hỏi đang dở mà không tính là thiếu quảng cáo (ví dụ người xem lướt đi, trình phát bị gỡ). */
export const releasePreRollSlot = () => {
  inFlightSince = null;
};

/** Chỉ cho test. */
export const resetPreRollFrequency = () => {
  memory = null;
  storageBroken = false;
  inFlightSince = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* bỏ qua */
  }
};
